/**
 * Designs: what a business has, and what it can start from.
 *
 * The order depends on whether there is any work yet. A new user needs to be shown
 * what this product can make, so the templates come first. Somebody with four designs
 * needs to get to one of them, and after that the quick ways of starting afresh — an
 * empty page, a document they already use — come before a gallery they have already
 * seen, which is chrome by then.
 *
 * Starting a design against a dataset does the matching in the same request, so the
 * next screen is either the designer with their own data in it or the one or two
 * questions that genuinely need answering. Never a mapping form.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, type DatasetSummary, type FolderRow, type StarterSummary, type TemplateSummary } from "../api.js";
import { reportFailure } from "../App.js";
import { changed, useChanges } from "../changes.js";
import { FolderCrumbs, FolderTile } from "../shell/Folders.js";
import { folderView } from "../shell/tree.js";
import { go } from "../router.js";
import { Button, Card, Chip, Confirm, Icon, Note, Skeleton, Sketch, Thumb, count, toast, when } from "../ui/index.js";

/** How often, and how many times, the list asks again for pictures that are still being drawn. */
const THUMBNAIL_POLL_MS = 6_000;
const THUMBNAIL_POLLS = 10;
import { atLimit, upgradeTarget, useWorkspace } from "../workspace.js";

export function Designs({ preselectedDataset, folder, starter }: { preselectedDataset: string | null; folder: string | null; starter?: string | null }) {
  const { view, refresh } = useWorkspace();
  const changes = useChanges();
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [starters, setStarters] = useState<StarterSummary[] | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [datasets, setDatasets] = useState<DatasetSummary[] | null>(null);
  const [dataset, setDataset] = useState<string | null>(preselectedDataset);
  const [starting, setStarting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<TemplateSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.starters(), api.templates(), api.datasets(), api.folders()])
      .then(([s, t, d, f]) => {
        if (cancelled) return;
        setStarters(s.starters);
        setTemplates(t.templates);
        setDatasets(d.datasets);
        setFolders(f.folders);
        // One set of data and nothing chosen: choose it. Making somebody pick from
        // a list of one is a question with no information in it.
        if (!preselectedDataset && d.datasets.length === 1) setDataset(d.datasets[0]!.id);
      })
      .catch((e) => !cancelled && reportFailure(e, "We couldn't load your designs."));
    return () => {
      cancelled = true;
    };
  }, [preselectedDataset, changes]);

  /*
   * Pictures arrive a little after the designs do.
   *
   * The service draws a design's first page shortly after it changes, so a list
   * loaded straight after an edit — or on the first visit after thumbnails existed —
   * has blanks in it. Asking again a few times, and only while there are blanks,
   * fills them in without a reload; stopping after a minute means a design that
   * genuinely cannot be drawn does not cost a request every six seconds for ever.
   */
  const missing = templates?.some((t) => !t.thumbnail) ?? false;
  useEffect(() => {
    if (!missing) return;
    let cancelled = false;
    let polls = 0;
    let timer: ReturnType<typeof setTimeout>;
    const again = () => {
      timer = setTimeout(async () => {
        if (cancelled || ++polls > THUMBNAIL_POLLS) return;
        try {
          const { templates: fresh } = await api.templates();
          if (cancelled) return;
          setTemplates(fresh);
          if (fresh.some((t) => !t.thumbnail)) again();
        } catch {
          // A picture is not worth an error message.
        }
      }, THUMBNAIL_POLL_MS);
    };
    again();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [missing]);

  const full = atLimit(view, "templates");
  const upgrade = upgradeTarget(view);

  const place = useMemo(
    () =>
      folderView(
        folders.filter((f) => f.section === "designs"),
        (templates ?? []).map((t) => ({ id: t.id, name: t.name, folderId: t.folderId, updatedAt: t.updatedAt })),
        folder
      ),
    [folders, templates, folder]
  );

  const start = async (starterId?: string) => {
    setStarting(starterId ?? "blank");
    try {
      const created = await api.createTemplate({
        ...(starterId ? { starterId } : {}),
        ...(dataset ? { datasetId: dataset } : {})
      });
      await refresh();
      // Straight into the designer. The matching already happened server-side; if
      // anything needs asking, the designer's own banner offers it rather than a
      // screen standing between the user and their document.
      go(`/designs/${created.template.id}`);
    } catch (e) {
      setStarting(null);
      reportFailure(e, "We couldn't start that design.");
    }
  };

  /*
   * `?starter=invoice`, from a free document page on the site. Opened once, exactly as
   * clicking its tile would — so the plan's design limit is the service's to enforce,
   * as it is for a click — and an id that is not a starter is ignored, leaving the
   * gallery as it always is.
   */
  const openedStarter = useRef(false);
  useEffect(() => {
    if (openedStarter.current || !starter || !starters || !templates || !datasets) return;
    if (!starters.some((s) => s.id === starter)) return;
    openedStarter.current = true;
    void start(starter);
  }, [starter, starters, templates, datasets]);

  if (!starters || !templates || !datasets) {
    return (
      <div className="wrap stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 36, width: 200 }} />
        <Skeleton rows={2} height={150} />
      </div>
    );
  }

  const noData = datasets.length === 0;

  const gallery = (
    <section className="stack" style={{ gap: 14 }}>
      <h2>{templates.length ? "Use our templates" : "Finished documents to start from"}</h2>
      <div className="grid grid--3">
        {starters.map((starter) => (
          <button
            key={starter.id}
            type="button"
            className="tile"
            disabled={full || starting !== null}
            onClick={() => void start(starter.id)}
            style={{ display: "flex", flexDirection: "column", gap: 12 }}
          >
            <Sketch marks={starter.sketch} />
            <div className="stack" style={{ gap: 5 }}>
              <strong>{starter.name}</strong>
              <span className="small muted" style={{ lineHeight: 1.45 }}>
                {starter.summary}
              </span>
              <span className="small muted" style={{ lineHeight: 1.4, color: "var(--ink-3)" }}>
                <em>{starter.wants}</em>
              </span>
            </div>
            {starting === starter.id ? (
              <span className="row small muted" style={{ gap: 8 }}>
                <span className="spinner" /> Opening…
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </section>
  );

  const emptyPage = (
    <Card flat>
      <div className="card__body spread" style={{ flexWrap: "wrap", gap: 14 }}>
        <div className="stack" style={{ gap: 4 }}>
          <strong>Or start from an empty page</strong>
          <span className="small muted">Your own columns, laid out however you like. Drag a list onto the page and it becomes a table.</span>
        </div>
        <Button onClick={() => void start()} busy={starting === "blank"} disabled={full || starting !== null} icon="plus">
          Empty page
        </Button>
      </div>
    </Card>
  );

  const fromDocument = (
    <Card flat>
      <div className="card__body spread" style={{ flexWrap: "wrap", gap: 14 }}>
        <div className="stack" style={{ gap: 4 }}>
          <strong>Or start from a document you already have</strong>
          <span className="small muted">
            Import a Word document or a PDF and BroadPaper turns it into a design you can edit
            {view.limits.importsPerMonth > 0 ? "." : " — part of Pro, and you can see what it would give you first."}
          </span>
        </div>
        <a className="btn" href="/designs/import">
          <Icon name="upload" /> Import a Word document or a PDF
        </a>
      </div>
    </Card>
  );

  return (
    <div className="wrap stack" style={{ gap: 26 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">Designs</span>
          <h1>{templates.length ? "Your designs" : "Choose something to start from"}</h1>
          <p className="head__lede">
            {templates.length
              ? "Open one to edit it, or start something new below."
              : "Every one of these is a finished document with sample data in it. Pick the closest and make it yours — nothing here is a wireframe."}
          </p>
        </div>
        {datasets.length > 1 ? (
          <div className="head__actions">
            {/* Which data a new design should use. Only asked when there is
                genuinely a choice. */}
            <label className="field" style={{ minWidth: 220 }}>
              <span className="field__label">Use this data</span>
              <select className="select" value={dataset ?? ""} onChange={(e) => setDataset(e.target.value || null)}>
                <option value="">Choose later</option>
                {datasets.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
      </div>

      {noData ? (
        <Note tone="info" title="You have not brought in any data yet">
          You can still open a design and see how it looks — it comes with sample data. Bring in your own spreadsheet when you are ready and BroadPaper
          will fill the document in from it. <a href="/data/new">Bring in data →</a>
        </Note>
      ) : null}

      {full ? (
        <Note tone="ask" title={`You are using all ${view.limits.templates} designs your plan includes`}>
          Delete one you no longer need{upgrade ? `, or move to ${upgrade.name} for ${count(upgrade.limits.templates)}.` : "."}
        </Note>
      ) : null}

      {templates.length ? (
        <section className="stack" style={{ gap: 14 }}>
          {place.crumbs.length ? <FolderCrumbs base="/designs" label="Your designs" crumbs={place.crumbs} /> : <h2>Your designs</h2>}
          <div className="grid grid--3">
            {place.folders.map((f) => (
              <FolderTile key={f.id} base="/designs" folder={f} noun="design" />
            ))}
            {templates.filter((t) => place.itemIds.has(t.id)).map((template) => (
              /*
                A div rather than an anchor, with the link inside it.
                
                The tile has to hold a delete button, and a button inside an anchor
                is neither valid nor clickable — the anchor swallows it. The plan
                limit note has told people to "delete one you no longer need" since
                the first version, with nothing anywhere to delete one with.
              */
              <div key={template.id} className="tile" style={{ display: "block" }}>
                <a href={`/designs/${template.id}`} style={{ textDecoration: "none", color: "inherit", display: "block" }}>
                  <Thumb thumbnail={template.thumbnail} label={template.name} />
                  <div className="stack" style={{ gap: 8 }}>
                    <div className="spread">
                      <strong style={{ fontSize: 16 }}>{template.name}</strong>
                      <Icon name="chevron" />
                    </div>
                    <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                      {template.importedFrom ? (
                        <Chip>Imported from {template.importedFrom.length > 28 ? `${template.importedFrom.slice(0, 26)}…` : template.importedFrom}</Chip>
                      ) : template.startedFrom ? (
                        <Chip>{template.startedFrom}</Chip>
                      ) : (
                        <Chip>From scratch</Chip>
                      )}
                      {template.dataset ? <Chip tone="good">{template.dataset.name}</Chip> : <Chip tone="ask">Needs data</Chip>}
                    </div>
                  </div>
                </a>
                <div className="spread" style={{ marginTop: 10 }}>
                  <span className="small muted">Edited {when(template.updatedAt)}</span>
                  <div className="row" style={{ gap: 2 }}>
                    <a className="btn btn--quiet btn--sm" href={`/designs/${template.id}/history`} title={`Earlier versions of “${template.name}”`} aria-label={`History of “${template.name}”`}>
                      <Icon name="history" />
                    </a>
                    <Button size="sm" variant="quiet" icon="trash" title={`Delete “${template.name}”`} onClick={() => setDeleting(template)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {!place.folders.length && !templates.some((t) => place.itemIds.has(t.id)) ? <p className="muted">Nothing in this folder yet. Move a design here from the sidebar.</p> : null}
        </section>
      ) : null}

      {templates.length ? (
        <>
          {emptyPage}
          {fromDocument}
          {gallery}
        </>
      ) : (
        <>
          {gallery}
          {emptyPage}
          {fromDocument}
        </>
      )}

      {deleting ? (
        <Confirm
          title={`Delete “${deleting.name}”?`}
          confirmLabel="Delete it"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            const target = deleting;
            setDeleting(null);
            try {
              await api.deleteTemplate(target.id);
              setTemplates((current) => (current ?? []).filter((t) => t.id !== target.id));
              await refresh();
              changed();
              toast("Deleted.", "good");
            } catch (e) {
              reportFailure(e, "We couldn't delete that.");
            }
          }}
        >
          The design and its history go for good. Documents you have already produced from it are not affected — they stay in your Documents, and they
          stay downloadable.
        </Confirm>
      ) : null}
    </div>
  );
}
